import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { setCurrentStep, validateAndSetStep } from '../../store/slices/bookSlice';
import { useBookAPI } from '../../hooks/useBookAPI';
import Header from './components/Header';
import BookInfoStep from './components/BookInfoStep';
import BookFormFactorStep from './components/BookFormFactorStep';
import BookFormattingStep from './components/BookFormattingStep';
import BookPreviewStep from './components/BookPreviewStep';
import BookCoverStep from './components/BookCoverStep';
import PricingStep from './components/PricingStep';
import ActionButtons from './components/ActionButtons';
import SuccessModal from './components/SuccessModal';
import RejectionBanner from './components/RejectionBanner';
import { X } from 'lucide-react';

interface Step {
  id: string;
  label: string;
}

const allSteps: Step[] = [
  { id: 'book-info', label: 'Book Info' },
  { id: 'book-form-factor', label: 'Book Form Factor' },
  { id: 'book-formatting', label: 'Book Formatting' },
  { id: 'book-preview', label: 'Preview' },
  { id: 'book-cover', label: 'Book Cover' },
  { id: 'pricing', label: 'Pricing' }
];

const Book: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookIdParam = searchParams.get('id');
  const stepParam   = searchParams.get('step');

  const currentStep = useAppSelector((state) => state.book.currentStep);
  const bookStatus = useAppSelector((state) => state.book.bookStatus);
  const bookData = useAppSelector((state) => state.book.bookData);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const hasEditorContent = !!bookData.content?.html && bookData.content.html.trim().length > 0;

  const { isLoading, error, loadBook } = useBookAPI();
  const [loadFailed, setLoadFailed] = useState(false);

  // Track when the book fetch specifically fails (distinct from submit/save errors)
  useEffect(() => {
    if (!isLoading && error && bookIdParam) {
      setLoadFailed(true);
    }
    if (isLoading) {
      setLoadFailed(false);
    }
  }, [isLoading, error, bookIdParam]);

  // Check if book is editable (only draft and rejected status)
  const isEditable = !bookIdParam || !bookStatus || ['draft', 'rejected'].includes(bookStatus);

  // For rejected books being resubmitted, hide the pricing step
  const isResubmission = bookStatus === 'rejected';
  const steps = useMemo(() => {
    // Uploaded-file books never use Preview (manual page-count entry instead) —
    // hide the step so it doesn't appear as an always-skipped dead entry. Editor-
    // content books always get Preview, whether or not approval is required.
    let filtered = hasEditorContent ? allSteps : allSteps.filter(step => step.id !== 'book-preview');
    if (isResubmission) {
      filtered = filtered.filter(step => step.id !== 'pricing');
    }
    return filtered;
  }, [isResubmission, hasEditorContent]);

  // Load book if editing an existing one. When a ?step= param is present
  // (e.g. returning from the cover designer), override the step AFTER the
  // fetch resolves so fetchBook.fulfilled cannot clobber it.
  useEffect(() => {
    if (!bookIdParam) return;
    loadBook(bookIdParam).then(() => {
      if (stepParam && allSteps.some(s => s.id === stepParam)) {
        dispatch(setCurrentStep(stepParam));
      }
    });
  // loadBook is stable (useCallback with no changing deps), so this is safe.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookIdParam]);

  // Show loading state while fetching book
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading book...</p>
        </div>
      </div>
    );
  }

  // Show error state only when the book fetch itself failed (not submit/save errors)
  if (loadFailed && bookIdParam) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-red-500 text-5xl mb-4">!</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to load book</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-gray-900 text-white px-6 py-2.5 rounded-lg hover:bg-gray-800 font-medium transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Show message if book is not editable
  if (!isEditable && bookIdParam) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-yellow-500 text-5xl mb-4">⚠</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Book Cannot Be Edited</h2>
          <p className="text-gray-600 mb-6">
            This book has status "<span className="font-medium">{bookStatus}</span>" and cannot be edited.
            Only books in draft or rejected status can be modified.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-gray-900 text-white px-6 py-2.5 rounded-lg hover:bg-gray-800 font-medium transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'book-info':
        return <BookInfoStep />;
      case 'book-form-factor':
        return <BookFormFactorStep />;
      case 'book-formatting':
        return <BookFormattingStep />;
      case 'book-preview':
        return <BookPreviewStep />;
      case 'book-cover':
        return <BookCoverStep />;
      case 'pricing':
        return <PricingStep />;
      default:
        return <BookInfoStep />;
    }
  };

  return (
    <div className="min-h-screen bg-white md:bg-gray-50">
      <Header />

      <div className="flex">
        {/* Mobile Sidebar Overlay */}
        {isSidebarOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Sidebar Navigation - Mobile Only */}
        <div
          className={`
            fixed inset-y-0 left-0 z-50
            w-64 bg-white border-r border-gray-200
            transform transition-transform duration-300 ease-in-out
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            md:hidden
          `}
        >
          <div className="p-4">
            {/* Mobile Close Button */}
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="absolute top-4 right-4 text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>

            <nav className="space-y-2 mt-8">
              {steps.map((step) => (
                <button
                  key={step.id}
                  onClick={() => {
                    dispatch(validateAndSetStep(step.id));
                    setIsSidebarOpen(false);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    currentStep === step.id
                      ? 'bg-orange-500 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {step.label}
                </button>
              ))}
            </nav>

          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-0 md:px-6 md:py-8 max-w-7xl md:mx-auto w-full">
          {/* Rejection Banner - Shows when book status is 'rejected' */}
          <RejectionBanner />
          {renderCurrentStep()}
        </div>
      </div>

      <ActionButtons />
      <SuccessModal />
    </div>
  );
};

export default Book;
